import { Controller } from "@hotwired/stimulus"
import { loadJsQR } from "../lib/lazy_qr"
import { showNativeAlert } from "../lib/bridge_utils"

export default class extends Controller {
  static targets = ["cameraInput", "fileInput", "errorBanner", "errorMessage"]

  // Stimulus target declarations
  declare readonly cameraInputTarget: HTMLInputElement
  declare readonly fileInputTarget: HTMLInputElement
  declare readonly errorBannerTarget: HTMLElement
  declare readonly errorMessageTarget: HTMLElement
  declare readonly hasCameraInputTarget: boolean
  declare readonly hasFileInputTarget: boolean
  declare readonly hasErrorBannerTarget: boolean
  declare readonly hasErrorMessageTarget: boolean

  openCamera() {
    this.cameraInputTarget.click()
  }

  openFile() {
    this.fileInputTarget.click()
  }

  openScannerModal(event: Event) {
    event.preventDefault()
    window.dispatchEvent(new CustomEvent("open-scanner"))
  }

  async process(event: Event) {
    this.hideError()
    const input = event.target as HTMLInputElement
    if (!input.files || input.files.length === 0) return

    const file = input.files[0]
    const imageData = await this.readImage(file)
    if (!imageData) {
      this.showMessage("Failed to read image")
      return
    }

    // Lazy load jsQR only when needed
    const jsQR = await loadJsQR()
    const code = jsQR(imageData.data, imageData.width, imageData.height)
    if (code) {
      this.handleSuccess(code.data, { source: "web_qr_scanner" })
    } else {
      this.showMessage("No QR code found in image")
    }
    
    // Reset input
    input.value = ""
  }

  private readImage(file: File): Promise<ImageData | null> {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement("canvas")

          const naturalWidth = (img as any).naturalWidth || img.width
          const naturalHeight = (img as any).naturalHeight || img.height

          // Scale up small images so jsQR has enough pixels to work with.
          // Also helps with SVGs that may have an awkward intrinsic size.
          const maxDim = 1024
          const srcW = Math.max(1, Number(naturalWidth) || 1)
          const srcH = Math.max(1, Number(naturalHeight) || 1)
          const scale = Math.max(1, Math.floor(maxDim / Math.max(srcW, srcH)))

          canvas.width = srcW * scale
          canvas.height = srcH * scale
          const ctx = canvas.getContext("2d")
          if (!ctx) {
            resolve(null)
            return
          }

          ctx.imageSmoothingEnabled = false
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
          resolve(ctx.getImageData(0, 0, canvas.width, canvas.height))
        }
        img.onerror = () => resolve(null)
        img.src = e.target?.result as string
      }
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(file)
    })
  }

  handleBridgeScan(event: CustomEvent) {
    this.hideError()
    const barcode = event.detail?.barcode
    if (!barcode) {
      this.showMessage("No barcode returned from scanner")
      return
    }

    this.handleSuccess(barcode, { source: "native_barcode_scanner" })
  }

  handleNativeScan(event: CustomEvent) {
    this.hideError()
    // Adapter for bridge-components scanner
    // The payload structure depends on the library, usually event.detail.code
    const code = event.detail?.code || event.detail?.data
    if (!code) {
       console.warn("Native scan event received but no code found in detail:", event.detail)
       return 
    }
    this.handleSuccess(code, { source: "native_scanner_pro" })
  }

  private hideError() {
    if (this.hasErrorBannerTarget) {
      this.errorBannerTarget.classList.add('hidden')
      this.errorBannerTarget.classList.remove('flex')
    }
  }

  private handleSuccess(data: string, context: Record<string, unknown> = {}) {
    // If it's a URL, redirect to it
    if (data.startsWith("http")) {
      window.location.href = data
      return
    }

    // Absolute/relative paths should also be navigated
    if (data.startsWith("/")) {
      window.location.href = data
      return
    }

    // If it looks like a rotating QR payload (encoded pacta-qr token), POST to scan endpoint
    if (this.isRotatingPayload(data)) {
      void this.postRotatingPayload(data, context)
      return
    }

    // Otherwise, try to navigate (legacy behavior)
    window.location.href = data
  }

  private isRotatingPayload(data: string): boolean {
    try {
      // Convert base64-url to standard base64 and pad
      let b = data.replace(/-/g, "+").replace(/_/g, "/")
      const mod = b.length % 4
      if (mod !== 0) b += "=".repeat(4 - mod)

      const decoded = atob(b)
      return decoded.startsWith("pacta-qr|v1|")
    } catch (e) {
      return false
    }
  }

  private async postRotatingPayload(payload: string, context: Record<string, unknown>): Promise<void> {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

    try {
      const res = await fetch('/qr_tokens/scan', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ payload, scan_context: context })
      })

      if (res.ok) {
        const body = await res.json()
        const pact = body.pact
        showNativeAlert(this.element as HTMLElement, { title: 'Success', message: 'QR scanned successfully', buttonText: 'OK', isDestructive: false })
        
        // Wait for alert/toast before redirecting if possible, but for now we redirect immediately
        // The native alert might persist or blocking.
        // Actually, if we show a native alert that requires "OK", we should wait?
        // But the bridge method is fire-and-forget in this implementation unless we use the promise version (which we can't easily here).
        
        if (pact && pact.id) {
          setTimeout(() => {
             window.location.href = `/pacts/${pact.id}`
          }, 1500)
        }
      } else {
        const err = await res.json().catch(() => null)
        const msg = err?.error || 'QR scan failed';
        this.showMessage(msg)
        showNativeAlert(this.element as HTMLElement, { title: 'Error', message: msg, buttonText: 'OK', isDestructive: true })
      }
    } catch (e) {
      this.showMessage('Network error scanning QR')
      showNativeAlert(this.element as HTMLElement, { title: 'Error', message: 'Network error scanning QR', buttonText: 'OK', isDestructive: true })
    }
  }

  // Prefer non-blocking toast notifications over alert() to avoid blocking UI.
  // Dispatch a `show-toast` CustomEvent (bubbles) so a notification controller
  // can listen for it and render a toast or native toast accordingly.
  private showMessage(message: string) {
    if (this.hasErrorBannerTarget && this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorBannerTarget.classList.remove('hidden')
      this.errorBannerTarget.classList.add('flex')
      return
    }

    const event = new CustomEvent('show-toast', {
      cancelable: true,
      bubbles: true,
      detail: { message }
    })

    const handled = !this.element.dispatchEvent(event)

    // If nothing handled the toast event, fall back to something visible.
    if (!handled) {
      console.warn(`QR scanner: ${message}`)
      // Keep this dev-only to avoid annoying production users.
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        window.alert(message)
      }
    }
  }
}
